/**
 * CodeInputField - Generic input component for single-field code arrays
 *
 * Manages a list of code items (e.g., ICD-10, CPT codes) with add/remove functionality.
 * Used by DiagnosisInputField and ProcedureInputField.
 *
 * Exports:
 * - CodeInputField: Main input component
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface CodeItem {
  code?: string;
  name?: string;
}

export interface CodeInputFieldConfig {
  /** Label for the code field (e.g., "ICD-10 Code", "CPT Code") */
  label: string;
  /** Placeholder text for the input (e.g., "e.g., M06.9") */
  placeholder: string;
  /** Message shown when no items exist (e.g., "No diagnosis codes added") */
  emptyMessage: string;
  /** Label for the add button (e.g., "Add Diagnosis Code") */
  addButtonLabel: string;
  /** Title for delete confirmation dialog (e.g., "Delete Diagnosis?") */
  deleteTitle: string;
  /** Description for delete confirmation dialog */
  deleteDescription: string;
  /** Prefix for item labels when code is empty (e.g., "Diagnosis", "Procedure") */
  itemLabelPrefix: string;
  /**
   * Whether the code field is marked required in the schema. Drives the `*`
   * indicator beside the label. Wrappers (DiagnosisInputField,
   * ProcedureInputField) read this from SchemaContext via `isFieldRequired`
   * and pass it in; CodeInputField itself stays schema-agnostic.
   */
  required?: boolean;
}

export interface CodeInputFieldProps {
  data: CodeItem[];
  onChange: (data: CodeItem[]) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  config: CodeInputFieldConfig;
}

// =============================================================================
// MAIN INPUT COMPONENT
// =============================================================================

/**
 * Create an empty code item
 */
const createEmptyItem = (): CodeItem => ({
  code: '',
});

export const CodeInputField: React.FC<CodeInputFieldProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
  config,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  const items = Array.isArray(data) ? data : [];

  // Slug-safe id prefix derived from the item label (e.g., "Diagnosis" -> "diagnosis")
  const inputIdPrefix = config.itemLabelPrefix.toLowerCase().replace(/\s+/g, '-');

  const handleAddItem = () => {
    const newItem = createEmptyItem();
    const newItems = [...items, newItem];
    onChange(newItems);
    // Auto-expand the newly added item
    setExpandedItems(prev => [...prev, `item-${items.length}`]);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    setDeleteConfirmIndex(null);
  };

  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], code: value };
    onChange(newItems);
  };

  const getItemLabel = (item: CodeItem, index: number): string => {
    if (item.code) return item.code;
    return `${config.itemLabelPrefix} ${index + 1}`;
  };

  if (items.length === 0) {
    return (
      <div className="py-2">
        <div className="text-sm text-muted-foreground italic mb-2">{config.emptyMessage}</div>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            {config.addButtonLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <Accordion
        type="multiple"
        value={expandedItems}
        onValueChange={setExpandedItems}
        className="space-y-1"
      >
        {items.map((item, index) => {
          const itemKey = `item-${index}`;
          const itemLabel = getItemLabel(item, index);

          return (
            <AccordionItem key={itemKey} value={itemKey} className="border rounded-md bg-muted/30">
              <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                <div className="flex items-center gap-2 flex-1">
                  <span>{itemLabel}</span>
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteConfirmIndex(index);
                    }}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div>
                  <label
                    htmlFor={`${inputIdPrefix}-${index}-code`}
                    className="text-xs text-muted-foreground mb-1 block"
                  >
                    {config.label}
                    {config.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                  <Input
                    id={`${inputIdPrefix}-${index}-code`}
                    type="text"
                    value={item.code || ''}
                    onChange={e => handleItemChange(index, e.target.value)}
                    placeholder={config.placeholder}
                    disabled={disabled}
                    className={`h-8 text-sm ${errors[`${index}.code`] ? 'border-destructive' : ''}`}
                  />
                  {errors[`${index}.code`] && (
                    <div className="text-xs text-destructive mt-1">{errors[`${index}.code`]}</div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Add button */}
      {!disabled && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            {config.addButtonLabel}
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmIndex !== null}
        onOpenChange={open => !open && setDeleteConfirmIndex(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{config.deleteTitle}</DialogTitle>
            <DialogDescription>{config.deleteDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmIndex !== null && handleDeleteItem(deleteConfirmIndex)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
